// Login sessions. Only the API checks the token, so a login stored in the browser
// keeps "working" after its token has expired (JWT_LIFETIME) and every save then
// fails with 401. These helpers catch that and send the user back to log in.
import axios from "axios";
import apiInstance from "@/api/axios";
import { store, persistor } from "@/store";
import { logoutUser } from "@/store/actions/user_actions";

// 401s from these are answers to the login forms themselves, not a dead session
const AUTH_FORMS = /auth\/(login|register|forget-password|otpverify|set-password|logout)/;
const EXPIRY_MARGIN_MS = 60 * 1000;

export const storedSession = () => {
  try {
    return JSON.parse(localStorage.getItem("AuthUser"));
  } catch {
    return null;
  }
};

// True when the token is missing, unreadable or past its exp claim.
export const tokenExpired = (token) => {
  if (!token || typeof token !== "string") return true;
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(part));
    return typeof payload.exp === "number" && payload.exp * 1000 - EXPIRY_MARGIN_MS <= Date.now();
  } catch {
    return true;
  }
};

// Admin pages go to the admin login, everything else opens the site's login modal.
export const expiredSessionUrl = (pathname) =>
  pathname.startsWith("/admin") ? "/admin/login?expired=1" : "/?login=1&expired=1";

let redirecting = false;
// Clears both copies of the login (localStorage and the persisted redux user), then
// reloads on the login page, which says why.
export const endSession = () => {
  if (redirecting) return;
  redirecting = true;
  localStorage.removeItem("AuthUser");
  store.dispatch(logoutUser());
  const go = () => window.location.assign(expiredSessionUrl(window.location.pathname));
  Promise.resolve(persistor.flush()).then(go, go);
};

const isOurApi = (url) => {
  const api = process.env.NEXT_PUBLIC_API_PATH || "";
  return !/^https?:\/\//.test(url) || (api && url.startsWith(api));
};

// Only a request that carried a token proves the stored login is dead; a call that
// forgot to send one must not log a valid user out.
const sentToken = (config) => {
  const headers = config?.headers;
  if (!headers) return false;
  const value =
    typeof headers.get === "function"
      ? headers.get("Authorization")
      : headers.Authorization || headers.authorization;
  return typeof value === "string" && /^Bearer\s/i.test(value);
};

const onResponseError = (error) => {
  const url = error?.config?.url || "";
  if (
    error?.response?.status === 401 &&
    isOurApi(url) &&
    !AUTH_FORMS.test(url) &&
    sentToken(error.config) &&
    storedSession()
  ) {
    endSession();
  }
  return Promise.reject(error);
};

let installed = false;
// Both the shared instance and the plain axios import are used across the site.
export const installSessionGuard = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use((res) => res, onResponseError);
  apiInstance.interceptors.response.use((res) => res, onResponseError);
};
